import React, { useState } from 'react';
import { 
  FileText, ChevronDown, ChevronUp, Shield, Truck, Store, 
  Users, CheckCircle, AlertTriangle, Scale, Mail, Phone
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const TermsAndConditions = ({ onBack, onAccept, showAcceptButton = false }) => {
  const [expandedSections, setExpandedSections] = useState({
    uplands: true,
    asap: false,
    partnership: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const lastUpdated = "January 11, 2026";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="mb-4">
          ← Back
        </Button>
      )}

      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-blue-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-800">Terms & Conditions</h1>
        <p className="text-gray-500 mt-2">Last updated: {lastUpdated}</p>
      </div>

      {/* Section 1: UPLANDS FOOD MARKET Terms */}
      <Card className="mb-4 overflow-hidden">
        <button
          onClick={() => toggleSection('uplands')}
          className="w-full p-6 flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Store className="w-6 h-6 text-green-600" />
            <div className="text-left">
              <h2 className="text-xl font-bold text-gray-800">UPLANDS FOOD MARKET</h2>
              <p className="text-sm text-gray-500">Terms between Customers and Store</p>
            </div>
          </div>
          {expandedSections.uplands ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.uplands && (
          <div className="p-6 space-y-6 text-sm text-gray-700">
            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">1</Badge>
                Acceptance of Terms
              </h3>
              <p>By accessing and using the UPLANDS FOOD MARKET application ("App"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.</p>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">2</Badge>
                Account Registration
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>You must provide accurate and complete information when creating an account</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must be at least 18 years old to create an account</li>
                <li>One account per person; multiple accounts may be terminated</li>
                <li>You agree to notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">3</Badge>
                Products and Pricing
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>All prices are in Canadian Dollars (CAD) and include applicable taxes where noted</li>
                <li>Product availability is subject to change without notice</li>
                <li>We reserve the right to correct pricing errors</li>
                <li>Images are for illustration purposes; actual products may vary slightly</li>
                <li>Certain products may have purchase quantity limits</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">4</Badge>
                Orders and Payment
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>All orders are subject to acceptance and availability</li>
                <li>We accept credit cards, debit cards, gift cards, e-Transfer, and cash (for pickup/in-store)</li>
                <li>Payment is required before order processing for delivery orders</li>
                <li>E-Transfer payments require verification before order fulfillment</li>
                <li>Gift card balances are non-refundable and non-transferable</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">5</Badge>
                Cancellations and Refunds
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Orders may be cancelled before the "Picking" stage at no charge</li>
                <li>Once picking has begun, cancellation fees may apply</li>
                <li>Refunds for damaged or incorrect items will be processed within 5-7 business days</li>
                <li>Perishable items cannot be returned unless damaged upon delivery</li>
                <li>Contact customer service within 24 hours of delivery for any issues</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">6</Badge>
                In-Store Pickup
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pickup orders must be collected within 24 hours of ready notification</li>
                <li>Valid ID and pickup code are required for order collection</li>
                <li>Uncollected orders after 48 hours may be cancelled and refunded (minus restocking fee)</li>
                <li>Perishable items not collected within 24 hours will be disposed of</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">7</Badge>
                User Conduct
              </h3>
              <p className="mb-2">You agree NOT to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use the App for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Harass, abuse, or threaten our staff or delivery personnel</li>
                <li>Provide false information or impersonate others</li>
                <li>Use automated systems to access the App without permission</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">8</Badge>
                Privacy and Data
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>We collect and process personal data in accordance with our Privacy Policy</li>
                <li>Your data is used to process orders, improve services, and communicate with you</li>
                <li>We do not sell your personal information to third parties</li>
                <li>You may request deletion of your data by contacting customer service</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">9</Badge>
                Limitation of Liability
              </h3>
              <p>UPLANDS FOOD MARKET shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services. Our maximum liability shall not exceed the value of the order in question.</p>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-700">10</Badge>
                Contact Information
              </h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-medium">UPLANDS FOOD MARKET</p>
                <p>1234 Merivale Road, Ottawa, ON K2G 0Z9</p>
                <p className="flex items-center gap-2 mt-2"><Mail className="w-4 h-4" /> support@uplandsfoodmarket.com</p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> +1 (613) 555-0123</p>
              </div>
            </section>
          </div>
        )}
      </Card>

      {/* Section 2: ASAP-247/365!LOGISTICS Delivery Terms */}
      <Card className="mb-4 overflow-hidden">
        <button
          onClick={() => toggleSection('asap')}
          className="w-full p-6 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-red-600" />
            <div className="text-left">
              <h2 className="text-xl font-bold text-gray-800">ASAP-247/365!LOGISTICS</h2>
              <p className="text-sm text-gray-500">Delivery Terms between Customers and Delivery Service</p>
            </div>
          </div>
          {expandedSections.asap ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.asap && (
          <div className="p-6 space-y-6 text-sm text-gray-700">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 font-medium">
                ASAP-247/365!LOGISTICS is an independent delivery service provider partnered with UPLANDS FOOD MARKET.
              </p>
            </div>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">1</Badge>
                Delivery Service Agreement
              </h3>
              <p>By selecting delivery for your order, you agree to the following terms with ASAP-247/365!LOGISTICS ("Delivery Partner"). These terms govern the delivery service portion of your order.</p>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">2</Badge>
                Delivery Zones and Fees
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Delivery is available within designated zones in the Ottawa area</li>
                <li>Delivery fees are calculated based on: base fee + distance rate + zone pricing</li>
                <li>Fees are displayed at checkout before order confirmation</li>
                <li>Priority and same-day deliveries incur additional charges</li>
                <li>Delivery fees are non-refundable once the driver has been dispatched</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">3</Badge>
                Delivery Times
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Priority:</strong> 2-4 hours from order confirmation</li>
                <li><strong>Same Day:</strong> Delivery by end of business day</li>
                <li><strong>Next Day:</strong> Delivery by end of next business day</li>
                <li><strong>Standard:</strong> 3-5 business days</li>
                <li>Delivery times are estimates and may vary due to weather, traffic, or high demand</li>
                <li>You will receive notifications when your order is out for delivery</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">4</Badge>
                Delivery Requirements
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Accurate delivery address must be provided at checkout</li>
                <li>Someone must be available to receive the order (unless contactless delivery is selected)</li>
                <li>For age-restricted items, valid ID must be presented upon delivery</li>
                <li>Access instructions (buzzer codes, gate codes) must be provided if applicable</li>
                <li>Delivery to commercial addresses may require reception confirmation</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">5</Badge>
                Failed Delivery Attempts
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Driver will attempt contact via phone/app if unable to deliver</li>
                <li>Orders may be left at door if contactless delivery was selected</li>
                <li>Failed deliveries due to incorrect address may incur redelivery fees</li>
                <li>After 2 failed attempts, order will be returned to store</li>
                <li>Perishable items from failed deliveries cannot be refunded</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">6</Badge>
                Proof of Delivery
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Drivers will capture photo proof of completed deliveries</li>
                <li>Photos may include the delivery location and package placement</li>
                <li>Proof of delivery is available in your order history</li>
                <li>Signature may be required for high-value orders</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">7</Badge>
                Raffle Ticket Promotion
              </h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <ul className="list-disc pl-5 space-y-1">
                  <li>Each completed delivery earns ONE (1) raffle ticket</li>
                  <li>Monthly prizes: 1st - $150, 2nd - $100, 3rd - $50</li>
                  <li>Draws held on the last day of each month</li>
                  <li>Winners notified via email within 48 hours of draw</li>
                  <li>Prizes must be claimed within 30 days</li>
                  <li>Employees of UPLANDS FOOD MARKET and ASAP-247/365!LOGISTICS are not eligible</li>
                  <li>Tickets are non-transferable and have no cash value</li>
                </ul>
              </div>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">8</Badge>
                Liability and Insurance
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>ASAP-247/365!LOGISTICS carries liability insurance for delivery operations</li>
                <li>Damaged items during transit will be replaced or refunded</li>
                <li>Claims must be reported within 24 hours of delivery with photo evidence</li>
                <li>Maximum liability is limited to the value of items in the order</li>
                <li>We are not liable for delays due to force majeure events</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">9</Badge>
                Driver Conduct
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Drivers are trained professionals representing ASAP-247/365!LOGISTICS</li>
                <li>Drivers will not enter your home unless explicitly invited</li>
                <li>Any inappropriate behavior should be reported immediately</li>
                <li>Tipping is appreciated but not required</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-red-100 text-red-700">10</Badge>
                Contact for Delivery Issues
              </h3>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-medium">ASAP-247/365!LOGISTICS</p>
                <p className="flex items-center gap-2 mt-2"><Mail className="w-4 h-4" /> delivery@asap247365.com</p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> +1 (613) 555-ASAP (2727)</p>
                <p className="text-xs text-gray-500 mt-2">Available 24/7/365</p>
              </div>
            </section>
          </div>
        )}
      </Card>

      {/* Section 3: Partnership Terms */}
      <Card className="mb-4 overflow-hidden">
        <button
          onClick={() => toggleSection('partnership')}
          className="w-full p-6 flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-purple-600" />
            <div className="text-left">
              <h2 className="text-xl font-bold text-gray-800">Partnership Agreement</h2>
              <p className="text-sm text-gray-500">Terms between UPLANDS FOOD MARKET & ASAP-247/365!LOGISTICS</p>
            </div>
          </div>
          {expandedSections.partnership ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.partnership && (
          <div className="p-6 space-y-6 text-sm text-gray-700">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-purple-700 font-medium">
                This section outlines the partnership agreement governing the relationship between UPLANDS FOOD MARKET and ASAP-247/365!LOGISTICS for the benefit of our customers.
              </p>
            </div>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">1</Badge>
                Service Level Agreement
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>ASAP-247/365!LOGISTICS commits to 95% on-time delivery rate</li>
                <li>Temperature-controlled vehicles for perishable items</li>
                <li>Real-time tracking available for all deliveries</li>
                <li>Maximum 2-hour delivery window for priority orders</li>
                <li>24/7/365 delivery service availability</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">2</Badge>
                Quality Standards
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>All drivers undergo background checks and food handling training</li>
                <li>Insulated delivery bags provided for all food orders</li>
                <li>Chain of custody documentation for order handoffs</li>
                <li>Regular vehicle inspections and maintenance</li>
                <li>COVID-19 and health safety protocols maintained</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">3</Badge>
                Customer Issue Resolution
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>UPLANDS FOOD MARKET handles product-related issues</li>
                <li>ASAP-247/365!LOGISTICS handles delivery-related issues</li>
                <li>Joint responsibility for damaged goods during transit</li>
                <li>Customer refunds processed within 5-7 business days</li>
                <li>Escalation path available for unresolved issues</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">4</Badge>
                Data Sharing and Privacy
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Customer data shared only as necessary for delivery completion</li>
                <li>Both parties comply with PIPEDA and applicable privacy laws</li>
                <li>Delivery data retained for 2 years for service improvement</li>
                <li>No customer data sold to third parties by either party</li>
              </ul>
            </section>

            <section>
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <Badge className="bg-purple-100 text-purple-700">5</Badge>
                Insurance and Liability
              </h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>ASAP-247/365!LOGISTICS maintains $2M commercial liability insurance</li>
                <li>Vehicle insurance coverage for all delivery vehicles</li>
                <li>Workers' compensation coverage for all drivers</li>
                <li>Product liability covered by UPLANDS FOOD MARKET</li>
              </ul>
            </section>
          </div>
        )}
      </Card>

      {/* Agreement Section */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Scale className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 mb-2">Governing Law</h3>
            <p className="text-sm text-gray-600 mb-4">
              These Terms and Conditions are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario, Canada.
            </p>
            <p className="text-xs text-gray-500">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </div>
        </div>

        {showAcceptButton && onAccept && (
          <div className="mt-6 pt-6 border-t border-blue-200">
            <Button 
              onClick={onAccept}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              I Accept the Terms & Conditions
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TermsAndConditions;
